import React, { FC, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';

import { DEFAULT_FILTERS } from '../../../../constants/ecp-locator';
import { useSetFilterParams } from '../../../../graphql/ecp-locator/actions/set-filter-params';
import {
  filterParams,
  setAutocompletedValue,
  setEcpFormSubmitted,
  setExternalSource,
  setAutocompletedSearch,
} from '../../../../graphql/ecp-locator/ecp-locator.cache';
import { useMyLocation } from '../../../../hooks/use-my-location';
import { useUpdateEcpStores } from '../../../../hooks/use-update-ecp-stores';
import { IFittingProps } from '../ecp-locator-settings.models';
import { EcpStoreType } from '../../../../rest/ecp-locator';
import { ServiceTypeFilters } from '../../../../graphql/ecp-locator/models/filter-params.models';

export const withDefaultFiltersAndStoresList = <T extends unknown>(Component: FC<T>) => (
  props: T & IFittingProps,
): JSX.Element => {
  const {
    isFitted,
    isLoading,
    isError,
    isFittingsLoaded = true,
  } = props;
  const [searchParams] = useSearchParams();
  const urlAddress = searchParams.get('address');
  const urlCity = searchParams.get('city');
  const urlSource = searchParams.get('source');
  const hasExternalAddress = !!(urlAddress?.trim());

  const { isLocationDefined, coords } = useMyLocation();
  const { setFilterParamsToCache } = useSetFilterParams();
  const [initLocationValue, setInitLocationValue] = useState(isLocationDefined);
  const { updateStores, isStoresLoading, isStoresError } = useUpdateEcpStores();
  const currentFilters = useReactiveVar(filterParams);
  const { isFirstTimeLoading } = currentFilters;
  const addressHandled = useRef(false);

  // Handle external address from URL query params (e.g. OZON store cards)
  useEffect(() => {
    if (!hasExternalAddress || addressHandled.current || !isFittingsLoaded) return;
    addressHandled.current = true;

    setFilterParamsToCache({
      ...currentFilters,
      isFirstTimeLoading: false,
      isFiltersChanged: true,
      services: EcpStoreType.fitter as ServiceTypeFilters,
    });

    if (urlSource) {
      setExternalSource(urlSource);
    }

    const searchQuery = urlCity
      ? `${urlCity.trim()}, ${urlAddress!.trim()}`
      : urlAddress!.trim();

    setAutocompletedValue(searchQuery);
    setAutocompletedSearch(true);
  }, [isFittingsLoaded]);

  // Standard flow: use browser geolocation (skip if external address is provided)
  useEffect(() => {
    if (hasExternalAddress) return;

    const isLocationAllowedClick = isLocationDefined && !initLocationValue;
    const isLocationDeniedByUser = !isLocationDefined && initLocationValue;

    // set default filters and stores
    if (isFittingsLoaded && isFirstTimeLoading) {
      const filters = {
        ...DEFAULT_FILTERS,
        isFiltersChanged: true,
        services: EcpStoreType.fitter as ServiceTypeFilters,
      };

      if (isLocationDefined) {
        updateStores({ filters, userLocation: coords });
        // Set value to notify error watcher if error should be shown.
        setEcpFormSubmitted(true);
      } else {
        setFilterParamsToCache(filters);
      }
    }

    // set stores if user click allow button on location browser popup
    if (isLocationAllowedClick) {
      updateStores({ userLocation: coords });
      setInitLocationValue(true);
      // Set value to notify error watcher if error should be shown.
      setEcpFormSubmitted(true);
    }

    // set default state if user deny location access in browser
    if (isLocationDeniedByUser) {
      setInitLocationValue(false);
    }
  }, [isFittingsLoaded, isFitted, isLocationDefined]);

  return (
    <Component
      {...props}
      isLoading={isLoading || isStoresLoading}
      isError={isError || isStoresError}
      updateStores={updateStores}
    />
  );
};
